// frontend/src/reservas/AdminReservas.js
import React, { useState, useEffect, useCallback } from 'react';
import { API_URL, API_ENDPOINTS } from '../config/api';
import './AdminReservas.css';
import '../styles/AdminTabs.css';
import {
    FiCalendar, FiHome, FiList, FiSettings, FiPieChart,
    FiPlus, FiEdit2, FiTrash2, FiEye, FiCheck, FiX,
    FiClock, FiUsers, FiDollarSign, FiImage, FiAlertCircle,
    FiRefreshCw, FiFilter, FiSearch, FiCheckCircle, FiChevronLeft, FiChevronRight
} from 'react-icons/fi';

const STATUS_LABELS = {
    pending: 'Pendiente',
    pre_approved: 'Pre-aprobada',
    payment_pending: 'Pago pendiente',
    payment_uploaded: 'Pago subido',
    approved: 'Aprobada',
    rejected: 'Rechazada',
    cancelled: 'Cancelada',
    completed: 'Completada',
    no_show: 'No se presento'
};

const STATUS_COLORS = {
    pending: '#ed8936',
    pre_approved: '#4299e1',
    payment_pending: '#d69e2e',
    payment_uploaded: '#dd6b20',
    approved: '#48bb78',
    rejected: '#e53e3e',
    cancelled: '#a0aec0',
    completed: '#38b2ac',
    no_show: '#718096'
};

const AREA_TYPES = {
    salon: 'Salon de Eventos',
    piscina: 'Piscina',
    cancha: 'Cancha',
    bbq: 'BBQ / Parrillera',
    gimnasio: 'Gimnasio',
    terraza: 'Terraza',
    parqueadero: 'Parqueadero',
    otro: 'Otro'
};

const DAYS_MAP = { 1: 'Lun', 2: 'Mar', 3: 'Mie', 4: 'Jue', 5: 'Vie', 6: 'Sab', 7: 'Dom' };

export default function AdminReservas({ user }) {
    const [activeTab, setActiveTab] = useState('solicitudes');
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);

    const [areas, setAreas] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [stats, setStats] = useState({});
    const [filterStatus, setFilterStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    const [showAreaModal, setShowAreaModal] = useState(false);
    const [editingArea, setEditingArea] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedReservation, setSelectedReservation] = useState(null);
    const [adminNotes, setAdminNotes] = useState('');

    // Calendar state
    const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth() + 1);
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
    const [calendarReservations, setCalendarReservations] = useState([]);
    const [selectedCalendarDay, setSelectedCalendarDay] = useState(null);

    // Area form
    const [areaForm, setAreaForm] = useState({
        name: '', type: 'otro', property_type: 'conjunto', description: '',
        capacity: 0, tariff: 0, tariff_type: 'por_evento', deposit: 0,
        rules: '', available_days: '1,2,3,4,5,6,7',
        available_from: '08:00', available_until: '22:00',
        min_hours: 1, max_hours: 8, advance_days: 3, max_advance_days: 30,
        requires_payment: true, auto_approve: false, is_active: true
    });
    const [areaPhotos, setAreaPhotos] = useState([]);
    const [galleryArea, setGalleryArea] = useState(null);
    const [galleryIndex, setGalleryIndex] = useState(0);

    const token = localStorage.getItem('token');

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 4000);
    };

    const authHeaders = useCallback(() => ({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    }), [token]);

    // ==================== FETCH ====================
    const fetchAreas = useCallback(async () => {
        try {
            const res = await fetch(API_ENDPOINTS.reservationAreas, { headers: authHeaders() });
            const data = await res.json();
            if (data.areas) setAreas(data.areas);
        } catch (err) { console.error('Error fetching areas:', err); }
    }, [authHeaders]);

    const fetchReservations = useCallback(async () => {
        try {
            let url = API_ENDPOINTS.reservations;
            if (filterStatus) url += `?status=${filterStatus}`;
            const res = await fetch(url, { headers: authHeaders() });
            const data = await res.json();
            if (data.reservations) setReservations(data.reservations);
        } catch (err) { console.error('Error fetching reservations:', err); }
    }, [authHeaders, filterStatus]);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(API_ENDPOINTS.reservationStats, { headers: authHeaders() });
            const data = await res.json();
            if (data.stats) setStats(data.stats);
        } catch (err) { console.error('Error fetching stats:', err); }
    }, [authHeaders]);

    const fetchCalendar = useCallback(async (month, year) => {
        try {
            const m = month || calendarMonth;
            const y = year || calendarYear;
            const res = await fetch(
                `${API_ENDPOINTS.reservationCalendar}?month=${m}&year=${y}`,
                { headers: authHeaders() }
            );
            const data = await res.json();
            if (data.reservations) setCalendarReservations(data.reservations);
        } catch (err) { console.error('Error fetching calendar:', err); }
    }, [authHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchAreas(), fetchReservations(), fetchStats()]);
            setLoading(false);
        };
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (activeTab === 'calendario') fetchCalendar(calendarMonth, calendarYear);
    }, [activeTab, calendarMonth, calendarYear]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchReservations();
    }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    // ==================== AREA CRUD ====================
    const resetAreaForm = () => {
        setAreaForm({
            name: '', type: 'otro', property_type: 'conjunto', description: '',
            capacity: 0, tariff: 0, tariff_type: 'por_evento', deposit: 0,
            rules: '', available_days: '1,2,3,4,5,6,7',
            available_from: '08:00', available_until: '22:00',
            min_hours: 1, max_hours: 8, advance_days: 3, max_advance_days: 30,
            requires_payment: true, auto_approve: false, is_active: true
        });
        setAreaPhotos([]);
        setEditingArea(null);
    };

    const openNewAreaModal = () => {
        resetAreaForm();
        setShowAreaModal(true);
    };

    const openEditAreaModal = (area) => {
        setEditingArea(area);
        setAreaForm({
            name: area.name, type: area.type, property_type: area.property_type || 'conjunto',
            description: area.description || '', capacity: area.capacity || 0,
            tariff: area.tariff || 0, tariff_type: area.tariff_type || 'por_evento',
            deposit: area.deposit || 0, rules: area.rules || '',
            available_days: area.available_days || '1,2,3,4,5,6,7',
            available_from: (area.available_from || '08:00:00').substring(0, 5),
            available_until: (area.available_until || '22:00:00').substring(0, 5),
            min_hours: area.min_hours || 1, max_hours: area.max_hours || 8,
            advance_days: area.advance_days || 3, max_advance_days: area.max_advance_days || 30,
            requires_payment: area.requires_payment === 1, auto_approve: area.auto_approve === 1,
            is_active: area.is_active === 1
        });
        setAreaPhotos([]);
        setShowAreaModal(true);
    };

    const handleSaveArea = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            Object.entries(areaForm).forEach(([key, val]) => {
                formData.append(key, typeof val === 'boolean' ? (val ? '1' : '0') : val);
            });
            for (const photo of areaPhotos) {
                formData.append('photos', photo);
            }

            const url = editingArea
                ? `${API_ENDPOINTS.reservationAreas}/${editingArea.id}`
                : API_ENDPOINTS.reservationAreas;

            const res = await fetch(url, {
                method: editingArea ? 'PUT' : 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                showNotif(editingArea ? 'Area actualizada' : 'Area creada exitosamente');
                setShowAreaModal(false);
                resetAreaForm();
                fetchAreas();
            } else {
                const errData = await res.json();
                showNotif(errData.message || 'Error al guardar', 'error');
            }
        } catch (err) {
            showNotif('Error de conexion', 'error');
        }
    };

    const handleDeleteArea = async (areaId) => {
        if (!window.confirm('Desactivar esta area? No se eliminara, solo se ocultara de los residentes.')) return;
        try {
            const res = await fetch(`${API_ENDPOINTS.reservationAreas}/${areaId}`, {
                method: 'DELETE',
                headers: authHeaders()
            });
            if (res.ok) {
                showNotif('Area desactivada');
                fetchAreas();
            }
        } catch (err) { showNotif('Error', 'error'); }
    };

    const handleReactivateArea = async (areaId) => {
        try {
            const res = await fetch(API_ENDPOINTS.reservationAreaReactivate(areaId), {
                method: 'PUT',
                headers: authHeaders()
            });
            if (res.ok) {
                showNotif('Area reactivada exitosamente');
                fetchAreas();
            }
        } catch (err) { showNotif('Error al reactivar', 'error'); }
    };

    const handleDeletePhoto = async (areaId, photoPath) => {
        if (!window.confirm('Eliminar esta foto permanentemente?')) return;
        try {
            const res = await fetch(API_ENDPOINTS.reservationAreaPhoto(areaId), {
                method: 'DELETE',
                headers: authHeaders(),
                body: JSON.stringify({ photo: photoPath })
            });
            if (res.ok) {
                const data = await res.json();
                showNotif('Foto eliminada');
                // Update editingArea state to reflect removal
                if (editingArea) {
                    setEditingArea({ ...editingArea, photos: data.area?.photos || [] });
                }
                fetchAreas();
            }
        } catch (err) { showNotif('Error al eliminar foto', 'error'); }
    };

    const openGallery = (area, index = 0) => {
        if (area.photos && area.photos.length > 0) {
            setGalleryArea(area);
            setGalleryIndex(index);
        }
    };

    const galleryNext = () => {
        if (galleryArea) {
            setGalleryIndex((galleryIndex + 1) % galleryArea.photos.length);
        }
    };

    const galleryPrev = () => {
        if (galleryArea) {
            setGalleryIndex((galleryIndex - 1 + galleryArea.photos.length) % galleryArea.photos.length);
        }
    };

    // ==================== RESERVATION STATUS ====================
    const handleStatusChange = async (reservationId, newStatus) => {
        try {
            const res = await fetch(API_ENDPOINTS.reservationStatus(reservationId), {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status: newStatus, admin_notes: adminNotes })
            });
            if (res.ok) {
                showNotif(`Reserva ${STATUS_LABELS[newStatus] || newStatus}`);
                setShowDetailModal(false);
                setAdminNotes('');
                fetchReservations();
                fetchStats();
            }
        } catch (err) { showNotif('Error al actualizar', 'error'); }
    };

    const openDetail = (reservation) => {
        setSelectedReservation(reservation);
        setAdminNotes('');
        setShowDetailModal(true);
    };

    // ==================== TOGGLE DAYS ====================
    const toggleDay = (day) => {
        const days = areaForm.available_days.split(',').filter(Boolean);
        const dayStr = String(day);
        if (days.includes(dayStr)) {
            setAreaForm({ ...areaForm, available_days: days.filter(d => d !== dayStr).join(',') });
        } else {
            setAreaForm({ ...areaForm, available_days: [...days, dayStr].sort().join(',') });
        }
    };

    // ==================== FILTER RESERVATIONS ====================
    const filteredReservations = reservations.filter(r => {
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return (r.user_name || '').toLowerCase().includes(s)
                || (r.area_name || '').toLowerCase().includes(s)
                || (r.ticket_number || '').toLowerCase().includes(s)
                || (r.user_apartment || '').toLowerCase().includes(s);
        }
        return true;
    });

    const pendingCount = reservations.filter(r => r.status === 'pending').length;
    const paymentUploadedCount = reservations.filter(r => r.status === 'payment_uploaded').length;

    // ==================== CALENDAR HELPERS ====================
    const getDaysInMonth = (m, y) => new Date(y, m, 0).getDate();
    const getFirstDayOfMonth = (m, y) => {
        const d = new Date(y, m - 1, 1).getDay();
        return d === 0 ? 6 : d - 1; // Monday=0
    };

    const calendarDays = [];
    const daysInMonth = getDaysInMonth(calendarMonth, calendarYear);
    const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
    for (let i = 0; i < firstDay; i++) calendarDays.push(null);
    for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

    const getReservationsForDay = (day) => {
        if (!day) return [];
        const dateStr = `${calendarYear}-${String(calendarMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return calendarReservations.filter(r => {
            const rDate = r.reservation_date ? r.reservation_date.substring(0, 10) : '';
            return rDate === dateStr;
        });
    };

    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    const prevMonth = () => {
        if (calendarMonth === 1) { setCalendarMonth(12); setCalendarYear(calendarYear - 1); }
        else setCalendarMonth(calendarMonth - 1);
        setSelectedCalendarDay(null);
    };
    const nextMonth = () => {
        if (calendarMonth === 12) { setCalendarMonth(1); setCalendarYear(calendarYear + 1); }
        else setCalendarMonth(calendarMonth + 1);
        setSelectedCalendarDay(null);
    };

    // ==================== FORMAT HELPERS ====================
    const formatDate = (d) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    const formatTime = (t) => {
        if (!t) return '';
        return t.substring(0, 5);
    };
    const formatCurrency = (amount) => `$${parseFloat(amount || 0).toFixed(2)}`;

    // ==================== RENDER ====================
    if (loading) {
        return (
            <div className="urbanq-reservas-admin-container">
                <div className="urbanq-reservas-admin-loading">
                    <FiRefreshCw className="spin" />
                    <p>Cargando reservas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="urbanq-reservas-admin-container">
            {/* Notification */}
            {notification && (
                <div className={`urbanq-reservas-admin-notification ${notification.type}`}>
                    {notification.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
                    <span>{notification.message}</span>
                    <button onClick={() => setNotification(null)}><FiX /></button>
                </div>
            )}

            {/* Tabs */}
            <div className="urbanq-admin-tabs">
                <button className={`urbanq-admin-tab ${activeTab === 'solicitudes' ? 'active' : ''}`}
                    onClick={() => setActiveTab('solicitudes')}>
                    <FiList /> Solicitudes
                    {pendingCount > 0 && <span className="urbanq-reservas-admin-badge">{pendingCount}</span>}
                </button>
                <button className={`urbanq-admin-tab ${activeTab === 'areas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('areas')}>
                    <FiHome /> Areas
                </button>
                <button className={`urbanq-admin-tab ${activeTab === 'estadisticas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('estadisticas')}>
                    <FiPieChart /> Estadisticas
                </button>
                <button className={`urbanq-admin-tab ${activeTab === 'calendario' ? 'active' : ''}`}
                    onClick={() => setActiveTab('calendario')}>
                    <FiCalendar /> Calendario
                </button>
            </div>

            {/* Content */}
            <div className="urbanq-reservas-admin-content">

                {/* ========== TAB: SOLICITUDES ========== */}
                {activeTab === 'solicitudes' && (
                    <div className="urbanq-reservas-admin-solicitudes">
                        <div className="urbanq-reservas-admin-filters">
                            <div className="urbanq-reservas-admin-search">
                                <FiSearch />
                                <input
                                    type="text"
                                    placeholder="Buscar por residente, area, ticket..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="urbanq-reservas-admin-filter-select">
                                <FiFilter />
                                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                                    <option value="">Todos los estados</option>
                                    <option value="pending">Pendientes</option>
                                    <option value="pre_approved">Pre-aprobadas</option>
                                    <option value="payment_uploaded">Pago subido</option>
                                    <option value="approved">Aprobadas</option>
                                    <option value="rejected">Rechazadas</option>
                                    <option value="completed">Completadas</option>
                                    <option value="cancelled">Canceladas</option>
                                </select>
                            </div>
                        </div>

                        {filteredReservations.length === 0 ? (
                            <div className="urbanq-reservas-admin-empty">
                                <FiCalendar />
                                <p>No hay solicitudes de reserva</p>
                            </div>
                        ) : (
                            <div className="urbanq-reservas-admin-table-container">
                                <table className="urbanq-reservas-admin-table">
                                    <thead>
                                        <tr>
                                            <th>Ticket</th>
                                            <th>Fecha</th>
                                            <th>Area</th>
                                            <th>Residente</th>
                                            <th>Depto.</th>
                                            <th>Horario</th>
                                            <th>Monto</th>
                                            <th>Estado</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReservations.map(r => (
                                            <tr key={r.id}>
                                                <td className="urbanq-reservas-admin-ticket">{r.ticket_number}</td>
                                                <td>{formatDate(r.reservation_date)}</td>
                                                <td>{r.area_name}</td>
                                                <td>{r.user_name}</td>
                                                <td>{r.user_apartment}</td>
                                                <td>{formatTime(r.start_time)} - {formatTime(r.end_time)}</td>
                                                <td>{formatCurrency(r.total_amount)}</td>
                                                <td>
                                                    <span className="urbanq-reservas-admin-status"
                                                        style={{ background: STATUS_COLORS[r.status] + '20', color: STATUS_COLORS[r.status], borderColor: STATUS_COLORS[r.status] }}>
                                                        {STATUS_LABELS[r.status]}
                                                    </span>
                                                </td>
                                                <td className="urbanq-reservas-admin-actions">
                                                    <button title="Ver detalle" onClick={() => openDetail(r)}><FiEye /></button>
                                                    {r.status === 'pending' && (
                                                        <>
                                                            <button title="Pre-aprobar" className="approve"
                                                                onClick={() => handleStatusChange(r.id, 'pre_approved')}>
                                                                <FiCheck />
                                                            </button>
                                                            <button title="Rechazar" className="reject"
                                                                onClick={() => handleStatusChange(r.id, 'rejected')}>
                                                                <FiX />
                                                            </button>
                                                        </>
                                                    )}
                                                    {r.status === 'payment_uploaded' && (
                                                        <button title="Aprobar pago" className="approve"
                                                            onClick={() => handleStatusChange(r.id, 'approved')}>
                                                            <FiCheckCircle />
                                                        </button>
                                                    )}
                                                    {r.status === 'approved' && (
                                                        <button title="Marcar completada" className="complete"
                                                            onClick={() => handleStatusChange(r.id, 'completed')}>
                                                            <FiCheck />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ========== TAB: AREAS ========== */}
                {activeTab === 'areas' && (
                    <div className="urbanq-reservas-admin-areas">
                        <div className="urbanq-reservas-admin-areas-header">
                            <h2>Areas Reservables ({areas.length})</h2>
                            <button className="urbanq-reservas-admin-btn-primary" onClick={openNewAreaModal}>
                                <FiPlus /> Nueva Area
                            </button>
                        </div>
                        <div className="urbanq-reservas-admin-areas-grid">
                            {areas.map(area => (
                                <div key={area.id} className={`urbanq-reservas-admin-area-card ${!area.is_active ? 'inactive' : ''}`}>
                                    <div className="urbanq-reservas-admin-area-image"
                                        onClick={() => openGallery(area, 0)}
                                        style={{ cursor: area.photos?.length > 0 ? 'pointer' : 'default' }}>
                                        {area.photos && area.photos.length > 0 ? (
                                            <div className="urbanq-reservas-admin-photo-stack">
                                                {area.photos.slice(0, 3).map((photo, i) => (
                                                    <img key={i}
                                                        src={`${API_URL}${photo}`}
                                                        alt={`${area.name} ${i + 1}`}
                                                        className={`urbanq-reservas-admin-stack-img stack-${i}`}
                                                        style={{
                                                            zIndex: 3 - i,
                                                            transform: `rotate(${i === 0 ? 0 : i === 1 ? -4 : 4}deg) translateX(${i === 0 ? 0 : i === 1 ? -8 : 8}px) scale(${1 - i * 0.04})`,
                                                        }}
                                                    />
                                                ))}
                                                {area.photos.length > 1 && (
                                                    <span className="urbanq-reservas-admin-photo-count">
                                                        <FiImage /> {area.photos.length}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="urbanq-reservas-admin-area-placeholder"><FiImage /></div>
                                        )}
                                        {!area.is_active && <span className="urbanq-reservas-admin-inactive-badge">Inactiva</span>}
                                    </div>
                                    <div className="urbanq-reservas-admin-area-info">
                                        <h3>{area.name}</h3>
                                        <span className="urbanq-reservas-admin-area-type">{AREA_TYPES[area.type] || area.type}</span>
                                        <div className="urbanq-reservas-admin-area-meta">
                                            <span><FiUsers /> {area.capacity} personas</span>
                                            <span><FiDollarSign /> {area.tariff_type === 'gratis' ? 'Gratis' : formatCurrency(area.tariff)}</span>
                                            <span><FiClock /> {formatTime(area.available_from)} - {formatTime(area.available_until)}</span>
                                        </div>
                                        <div className="urbanq-reservas-admin-area-actions">
                                            <button onClick={() => openEditAreaModal(area)}><FiEdit2 /> Editar</button>
                                            {area.is_active ? (
                                                <button className="delete" onClick={() => handleDeleteArea(area.id)}><FiTrash2 /> Desactivar</button>
                                            ) : (
                                                <button className="reactivate" onClick={() => handleReactivateArea(area.id)}><FiRefreshCw /> Reactivar</button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {areas.length === 0 && (
                                <div className="urbanq-reservas-admin-empty full">
                                    <FiHome />
                                    <p>No hay areas configuradas</p>
                                    <button className="urbanq-reservas-admin-btn-primary" onClick={openNewAreaModal}>
                                        <FiPlus /> Crear primera area
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ========== TAB: ESTADISTICAS ========== */}
                {activeTab === 'estadisticas' && (
                    <div className="urbanq-reservas-admin-stats">
                        <div className="urbanq-reservas-admin-stats-grid">
                            <div className="urbanq-reservas-admin-stat-card">
                                <div className="urbanq-reservas-admin-stat-icon total"><FiCalendar /></div>
                                <div className="urbanq-reservas-admin-stat-info">
                                    <h3>{stats.total || 0}</h3>
                                    <p>Total Reservas</p>
                                </div>
                            </div>
                            <div className="urbanq-reservas-admin-stat-card">
                                <div className="urbanq-reservas-admin-stat-icon pending"><FiClock /></div>
                                <div className="urbanq-reservas-admin-stat-info">
                                    <h3>{stats.pending || 0}</h3>
                                    <p>Pendientes</p>
                                </div>
                            </div>
                            <div className="urbanq-reservas-admin-stat-card">
                                <div className="urbanq-reservas-admin-stat-icon approved"><FiCheckCircle /></div>
                                <div className="urbanq-reservas-admin-stat-info">
                                    <h3>{stats.approved || 0}</h3>
                                    <p>Aprobadas</p>
                                </div>
                            </div>
                            <div className="urbanq-reservas-admin-stat-card">
                                <div className="urbanq-reservas-admin-stat-icon revenue"><FiDollarSign /></div>
                                <div className="urbanq-reservas-admin-stat-info">
                                    <h3>{formatCurrency(stats.total_revenue)}</h3>
                                    <p>Ingresos por Reservas</p>
                                </div>
                            </div>
                        </div>

                        <div className="urbanq-reservas-admin-stats-detail">
                            <div className="urbanq-reservas-admin-stat-row">
                                <span>Pre-aprobadas</span>
                                <span className="urbanq-reservas-admin-stat-value">{stats.pre_approved || 0}</span>
                            </div>
                            <div className="urbanq-reservas-admin-stat-row">
                                <span>Rechazadas</span>
                                <span className="urbanq-reservas-admin-stat-value">{stats.rejected || 0}</span>
                            </div>
                            <div className="urbanq-reservas-admin-stat-row">
                                <span>Completadas</span>
                                <span className="urbanq-reservas-admin-stat-value">{stats.completed || 0}</span>
                            </div>
                            <div className="urbanq-reservas-admin-stat-row">
                                <span>Areas configuradas</span>
                                <span className="urbanq-reservas-admin-stat-value">{areas.length}</span>
                            </div>
                            <div className="urbanq-reservas-admin-stat-row">
                                <span>Areas activas</span>
                                <span className="urbanq-reservas-admin-stat-value">{areas.filter(a => a.is_active).length}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* ========== TAB: CALENDARIO ========== */}
                {activeTab === 'calendario' && (
                    <div className="urbanq-reservas-admin-calendar">
                        <div className="urbanq-reservas-admin-calendar-header">
                            <button onClick={prevMonth}><FiChevronLeft /></button>
                            <h2>{monthNames[calendarMonth - 1]} {calendarYear}</h2>
                            <button onClick={nextMonth}><FiChevronRight /></button>
                        </div>
                        <div className="urbanq-reservas-admin-calendar-grid">
                            <div className="urbanq-reservas-admin-calendar-day-header">Lun</div>
                            <div className="urbanq-reservas-admin-calendar-day-header">Mar</div>
                            <div className="urbanq-reservas-admin-calendar-day-header">Mie</div>
                            <div className="urbanq-reservas-admin-calendar-day-header">Jue</div>
                            <div className="urbanq-reservas-admin-calendar-day-header">Vie</div>
                            <div className="urbanq-reservas-admin-calendar-day-header">Sab</div>
                            <div className="urbanq-reservas-admin-calendar-day-header">Dom</div>
                            {calendarDays.map((day, i) => {
                                const dayReservations = getReservationsForDay(day);
                                const isToday = day && new Date().getDate() === day
                                    && new Date().getMonth() + 1 === calendarMonth
                                    && new Date().getFullYear() === calendarYear;
                                return (
                                    <div key={i}
                                        className={`urbanq-reservas-admin-calendar-cell ${!day ? 'empty' : ''} ${isToday ? 'today' : ''} ${selectedCalendarDay === day ? 'selected' : ''}`}
                                        onClick={() => day && setSelectedCalendarDay(day)}>
                                        {day && (
                                            <>
                                                <span className="urbanq-reservas-admin-calendar-date">{day}</span>
                                                {dayReservations.length > 0 && (
                                                    <div className="urbanq-reservas-admin-calendar-dots">
                                                        {dayReservations.slice(0, 3).map((r, ri) => (
                                                            <span key={ri} className="urbanq-reservas-admin-calendar-dot"
                                                                style={{ background: STATUS_COLORS[r.status] }}
                                                                title={`${r.area_name} - ${r.user_name}`}></span>
                                                        ))}
                                                        {dayReservations.length > 3 && (
                                                            <span className="urbanq-reservas-admin-calendar-more">+{dayReservations.length - 3}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {selectedCalendarDay && (
                            <div className="urbanq-reservas-admin-calendar-detail">
                                <h3>Reservas del {selectedCalendarDay} de {monthNames[calendarMonth - 1]}</h3>
                                {getReservationsForDay(selectedCalendarDay).length === 0 ? (
                                    <p className="urbanq-reservas-admin-calendar-no-events">Sin reservas este dia</p>
                                ) : (
                                    <div className="urbanq-reservas-admin-calendar-events">
                                        {getReservationsForDay(selectedCalendarDay).map(r => (
                                            <div key={r.id} className="urbanq-reservas-admin-calendar-event">
                                                <div className="urbanq-reservas-admin-calendar-event-time">
                                                    {formatTime(r.start_time)} - {formatTime(r.end_time)}
                                                </div>
                                                <div className="urbanq-reservas-admin-calendar-event-info">
                                                    <strong>{r.area_name}</strong>
                                                    <span>{r.user_name} - {r.user_apartment}</span>
                                                </div>
                                                <span className="urbanq-reservas-admin-status"
                                                    style={{ background: STATUS_COLORS[r.status] + '20', color: STATUS_COLORS[r.status], borderColor: STATUS_COLORS[r.status] }}>
                                                    {STATUS_LABELS[r.status]}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ========== MODAL: AREA FORM ========== */}
            {showAreaModal && (
                <div className="urbanq-reservas-admin-modal-overlay" onClick={() => setShowAreaModal(false)}>
                    <div className="urbanq-reservas-admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="urbanq-reservas-admin-modal-header">
                            <h2>{editingArea ? 'Editar Area' : 'Nueva Area Reservable'}</h2>
                            <button onClick={() => setShowAreaModal(false)}><FiX /></button>
                        </div>
                        <form onSubmit={handleSaveArea} className="urbanq-reservas-admin-modal-body">
                            <div className="urbanq-reservas-admin-form-grid">
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Nombre del area *</label>
                                    <input type="text" required value={areaForm.name}
                                        onChange={e => setAreaForm({ ...areaForm, name: e.target.value })}
                                        placeholder="Ej: Salon de Eventos" />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Tipo</label>
                                    <select value={areaForm.type} onChange={e => setAreaForm({ ...areaForm, type: e.target.value })}>
                                        {Object.entries(AREA_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Tipo de propiedad</label>
                                    <select value={areaForm.property_type} onChange={e => setAreaForm({ ...areaForm, property_type: e.target.value })}>
                                        <option value="departamento">Departamento</option>
                                        <option value="casa">Casa</option>
                                        <option value="conjunto">Conjunto</option>
                                        <option value="edificio">Edificio</option>
                                        <option value="otro">Otro</option>
                                    </select>
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Capacidad (personas)</label>
                                    <input type="number" min="0" value={areaForm.capacity}
                                        onChange={e => setAreaForm({ ...areaForm, capacity: parseInt(e.target.value) || 0 })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Tarifa (USD)</label>
                                    <input type="number" min="0" step="0.01" value={areaForm.tariff}
                                        onChange={e => setAreaForm({ ...areaForm, tariff: parseFloat(e.target.value) || 0 })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Tipo de tarifa</label>
                                    <select value={areaForm.tariff_type} onChange={e => setAreaForm({ ...areaForm, tariff_type: e.target.value })}>
                                        <option value="por_hora">Por hora</option>
                                        <option value="por_dia">Por dia</option>
                                        <option value="por_evento">Por evento</option>
                                        <option value="gratis">Gratis</option>
                                    </select>
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Deposito / Garantia (USD)</label>
                                    <input type="number" min="0" step="0.01" value={areaForm.deposit}
                                        onChange={e => setAreaForm({ ...areaForm, deposit: parseFloat(e.target.value) || 0 })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Hora inicio</label>
                                    <input type="time" value={areaForm.available_from}
                                        onChange={e => setAreaForm({ ...areaForm, available_from: e.target.value })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Hora fin</label>
                                    <input type="time" value={areaForm.available_until}
                                        onChange={e => setAreaForm({ ...areaForm, available_until: e.target.value })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Min. horas por reserva</label>
                                    <input type="number" min="1" value={areaForm.min_hours}
                                        onChange={e => setAreaForm({ ...areaForm, min_hours: parseInt(e.target.value) || 1 })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Max. horas por reserva</label>
                                    <input type="number" min="1" value={areaForm.max_hours}
                                        onChange={e => setAreaForm({ ...areaForm, max_hours: parseInt(e.target.value) || 8 })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Anticipacion minima (dias)</label>
                                    <input type="number" min="0" value={areaForm.advance_days}
                                        onChange={e => setAreaForm({ ...areaForm, advance_days: parseInt(e.target.value) || 0 })} />
                                </div>
                                <div className="urbanq-reservas-admin-form-group">
                                    <label>Anticipacion maxima (dias)</label>
                                    <input type="number" min="1" value={areaForm.max_advance_days}
                                        onChange={e => setAreaForm({ ...areaForm, max_advance_days: parseInt(e.target.value) || 30 })} />
                                </div>
                            </div>

                            <div className="urbanq-reservas-admin-form-group full-width">
                                <label>Descripcion</label>
                                <textarea rows="3" value={areaForm.description}
                                    onChange={e => setAreaForm({ ...areaForm, description: e.target.value })}
                                    placeholder="Descripcion del area..." />
                            </div>

                            <div className="urbanq-reservas-admin-form-group full-width">
                                <label>Normas y reglas</label>
                                <textarea rows="4" value={areaForm.rules}
                                    onChange={e => setAreaForm({ ...areaForm, rules: e.target.value })}
                                    placeholder="Ingrese las normas de uso del area..." />
                            </div>

                            <div className="urbanq-reservas-admin-form-group full-width">
                                <label>Dias disponibles</label>
                                <div className="urbanq-reservas-admin-days-selector">
                                    {Object.entries(DAYS_MAP).map(([num, name]) => (
                                        <button type="button" key={num}
                                            className={areaForm.available_days.split(',').includes(num) ? 'active' : ''}
                                            onClick={() => toggleDay(num)}>
                                            {name}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="urbanq-reservas-admin-form-group full-width">
                                <label>Fotos del area</label>
                                <input type="file" multiple accept="image/*"
                                    onChange={e => setAreaPhotos(Array.from(e.target.files))} />
                                {editingArea && editingArea.photos && editingArea.photos.length > 0 && (
                                    <div className="urbanq-reservas-admin-existing-photos">
                                        {editingArea.photos.map((p, i) => (
                                            <div key={i} className="urbanq-reservas-admin-photo-thumb">
                                                <img src={`${API_URL}${p}`} alt={`Foto ${i + 1}`} />
                                                <button type="button"
                                                    className="urbanq-reservas-admin-photo-delete"
                                                    onClick={() => handleDeletePhoto(editingArea.id, p)}
                                                    title="Eliminar foto">
                                                    <FiX />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="urbanq-reservas-admin-form-toggles">
                                <label className="urbanq-reservas-admin-toggle">
                                    <input type="checkbox" checked={areaForm.requires_payment}
                                        onChange={e => setAreaForm({ ...areaForm, requires_payment: e.target.checked })} />
                                    <span>Requiere pago</span>
                                </label>
                                <label className="urbanq-reservas-admin-toggle">
                                    <input type="checkbox" checked={areaForm.is_active}
                                        onChange={e => setAreaForm({ ...areaForm, is_active: e.target.checked })} />
                                    <span>Area activa</span>
                                </label>
                            </div>

                            <div className="urbanq-reservas-admin-modal-footer">
                                <button type="button" className="urbanq-reservas-admin-btn-secondary"
                                    onClick={() => setShowAreaModal(false)}>Cancelar</button>
                                <button type="submit" className="urbanq-reservas-admin-btn-primary">
                                    {editingArea ? 'Actualizar' : 'Crear Area'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========== MODAL: GALERIA FOTOS ========== */}
            {galleryArea && (
                <div className="urbanq-reservas-admin-modal-overlay urbanq-reservas-gallery-overlay"
                    onClick={() => setGalleryArea(null)}>
                    <div className="urbanq-reservas-gallery-container" onClick={e => e.stopPropagation()}>
                        <button className="urbanq-reservas-gallery-close" onClick={() => setGalleryArea(null)}>
                            <FiX />
                        </button>
                        <h3 className="urbanq-reservas-gallery-title">{galleryArea.name}</h3>
                        <div className="urbanq-reservas-gallery-viewport">
                            {galleryArea.photos.length > 1 && (
                                <button className="urbanq-reservas-gallery-nav prev" onClick={galleryPrev}>
                                    <FiChevronLeft />
                                </button>
                            )}
                            <div className="urbanq-reservas-gallery-3d-scene">
                                {galleryArea.photos.map((photo, i) => {
                                    const offset = i - galleryIndex;
                                    const absOffset = Math.abs(offset);
                                    if (absOffset > 2) return null;
                                    return (
                                        <div key={i}
                                            className={`urbanq-reservas-gallery-slide ${offset === 0 ? 'active' : ''}`}
                                            style={{
                                                transform: `translateX(${offset * 60}%) translateZ(${-absOffset * 120}px) rotateY(${offset * -15}deg)`,
                                                opacity: offset === 0 ? 1 : 0.5,
                                                zIndex: 10 - absOffset,
                                                filter: offset === 0 ? 'none' : 'brightness(0.6)',
                                            }}
                                            onClick={() => setGalleryIndex(i)}>
                                            <img src={`${API_URL}${photo}`} alt={`${galleryArea.name} ${i + 1}`} />
                                        </div>
                                    );
                                })}
                            </div>
                            {galleryArea.photos.length > 1 && (
                                <button className="urbanq-reservas-gallery-nav next" onClick={galleryNext}>
                                    <FiChevronRight />
                                </button>
                            )}
                        </div>
                        <div className="urbanq-reservas-gallery-dots">
                            {galleryArea.photos.map((_, i) => (
                                <span key={i}
                                    className={`urbanq-reservas-gallery-dot ${i === galleryIndex ? 'active' : ''}`}
                                    onClick={() => setGalleryIndex(i)} />
                            ))}
                        </div>
                        <p className="urbanq-reservas-gallery-counter">{galleryIndex + 1} / {galleryArea.photos.length}</p>
                    </div>
                </div>
            )}

            {/* ========== MODAL: DETALLE RESERVA ========== */}
            {showDetailModal && selectedReservation && (
                <div className="urbanq-reservas-admin-modal-overlay" onClick={() => setShowDetailModal(false)}>
                    <div className="urbanq-reservas-admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="urbanq-reservas-admin-modal-header">
                            <h2>Detalle de Reserva - {selectedReservation.ticket_number}</h2>
                            <button onClick={() => setShowDetailModal(false)}><FiX /></button>
                        </div>
                        <div className="urbanq-reservas-admin-modal-body urbanq-reservas-admin-detail">
                            <div className="urbanq-reservas-admin-detail-grid">
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Residente</label>
                                    <span>{selectedReservation.user_name}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Departamento</label>
                                    <span>{selectedReservation.user_apartment}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Email</label>
                                    <span>{selectedReservation.user_email}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Area</label>
                                    <span>{selectedReservation.area_name}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Fecha</label>
                                    <span>{formatDate(selectedReservation.reservation_date)}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Horario</label>
                                    <span>{formatTime(selectedReservation.start_time)} - {formatTime(selectedReservation.end_time)}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Invitados</label>
                                    <span>{selectedReservation.num_guests}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Monto</label>
                                    <span>{formatCurrency(selectedReservation.total_amount)}</span>
                                </div>
                                <div className="urbanq-reservas-admin-detail-item">
                                    <label>Estado</label>
                                    <span className="urbanq-reservas-admin-status"
                                        style={{ background: STATUS_COLORS[selectedReservation.status] + '20', color: STATUS_COLORS[selectedReservation.status], borderColor: STATUS_COLORS[selectedReservation.status] }}>
                                        {STATUS_LABELS[selectedReservation.status]}
                                    </span>
                                </div>
                                {selectedReservation.purpose && (
                                    <div className="urbanq-reservas-admin-detail-item full">
                                        <label>Motivo</label>
                                        <span>{selectedReservation.purpose}</span>
                                    </div>
                                )}
                                {selectedReservation.notes && (
                                    <div className="urbanq-reservas-admin-detail-item full">
                                        <label>Notas del residente</label>
                                        <span>{selectedReservation.notes}</span>
                                    </div>
                                )}
                            </div>

                            {selectedReservation.payment_receipt && (
                                <div className="urbanq-reservas-admin-detail-receipt">
                                    <label>Comprobante de pago</label>
                                    <img src={`${API_URL}${selectedReservation.payment_receipt}`} alt="Comprobante" />
                                </div>
                            )}

                            <div className="urbanq-reservas-admin-detail-admin-notes">
                                <label>Notas del administrador</label>
                                <textarea rows="3" value={adminNotes}
                                    onChange={e => setAdminNotes(e.target.value)}
                                    placeholder="Agregar notas o razon de rechazo..." />
                            </div>

                            <div className="urbanq-reservas-admin-detail-actions">
                                {selectedReservation.status === 'pending' && (
                                    <>
                                        <button className="urbanq-reservas-admin-btn-success"
                                            onClick={() => handleStatusChange(selectedReservation.id, 'pre_approved')}>
                                            <FiCheck /> Pre-aprobar
                                        </button>
                                        <button className="urbanq-reservas-admin-btn-danger"
                                            onClick={() => handleStatusChange(selectedReservation.id, 'rejected')}>
                                            <FiX /> Rechazar
                                        </button>
                                    </>
                                )}
                                {selectedReservation.status === 'payment_uploaded' && (
                                    <button className="urbanq-reservas-admin-btn-success"
                                        onClick={() => handleStatusChange(selectedReservation.id, 'approved')}>
                                        <FiCheckCircle /> Aprobar Pago
                                    </button>
                                )}
                                {selectedReservation.status === 'approved' && (
                                    <>
                                        <button className="urbanq-reservas-admin-btn-success"
                                            onClick={() => handleStatusChange(selectedReservation.id, 'completed')}>
                                            <FiCheck /> Completar
                                        </button>
                                        <button className="urbanq-reservas-admin-btn-warning"
                                            onClick={() => handleStatusChange(selectedReservation.id, 'no_show')}>
                                            No se presento
                                        </button>
                                    </>
                                )}
                                {['pending', 'pre_approved'].includes(selectedReservation.status) && (
                                    <button className="urbanq-reservas-admin-btn-secondary"
                                        onClick={() => handleStatusChange(selectedReservation.id, 'cancelled')}>
                                        Cancelar Reserva
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
